<div class="container-fluid p-0">
  <nav class="navbar navbar-expand-lg navbar-light bg-light">
    <span class="navbar-brand">Access Requests</span>
    <button class="navbar-toggler" type="button" data-toggle="collapse" data-target="#accessRequestsNav" aria-controls="accessRequestsNav" aria-expanded="false" aria-label="Toggle navigation">
      <span class="navbar-toggler-icon"></span>
    </button>

    <div class="collapse navbar-collapse" id="accessRequestsNav">
      <span class="mr-auto">
        <!-- Both the menu and its trigger sit inside the SAME guard. A mat-menu defined outside its
             guard still renders into the CDK overlay even when the trigger is hidden. -->
        <ng-container *ngIf="canEdit">
          <mat-menu #bulkMenu="matMenu">
            <button mat-menu-item (click)="listDataSource.selectAllToggle()">
              <mat-icon>{{ listDataSource.isAllSelected() ? 'clear_all' : 'select_all' }}</mat-icon>
              {{ listDataSource.isAllSelected() ? 'Deselect' : 'Select' }} All
            </button>
            <button mat-menu-item (click)="onBulkApprove()" [disabled]="!listDataSource.selection.hasValue()">
              <mat-icon>check_circle</mat-icon>
              Approve Selected ({{ listDataSource.selection.selected.length }})
            </button>
            <button mat-menu-item (click)="onBulkDecline()" [disabled]="!listDataSource.selection.hasValue()">
              <mat-icon>block</mat-icon>
              Decline Selected ({{ listDataSource.selection.selected.length }})
            </button>
          </mat-menu>

          <button mat-button class="m-1 mr-2" [matMenuTriggerFor]="bulkMenu" style="min-width: 150px"
                  matTooltip="Use this menu to select requests and approve or decline them together.">
            <mat-icon>more_vert</mat-icon>
            List Menu
          </button>
        </ng-container>
      </span>

      <!-- Filters live behind a button, matching every other list. An inline mat-form-field here made this
           header taller than the rest of the product. -->
      <button mat-raised-button class="m-1 mr-lg-3" (click)="onOpenFilters()" *ngIf="canView"
              matTooltip="Click this button to display filter options for this list.">
        <mat-icon>filter_list</mat-icon> Filter
      </button>

      <form class="form-inline mx-1 my-0" (ngSubmit)="listDataSource.getData(true)" *ngIf="canView">
        <input
          class="form-control ml-0 mr-2 my-1"
          type="search"
          [(ngModel)]="listDataSource.search"
          name="search"
          placeholder="Search Requests"
          aria-label="Search"
          matTooltip="Search requests by name, email, mobile number or claimed company." />

        <button mat-raised-button color="secondary" type="submit" class="mx-0 my-1"
                matTooltip="Click this button to run the current search.">
          <mat-icon>search</mat-icon> Search
        </button>
      </form>
    </div>
  </nav>

  <div class="alert alert-warning m-2" *ngIf="!canView">
    <mat-icon class="align-middle mr-2">info</mat-icon>
    You do not have permission to view access requests.
    <span>Please contact your administrator to request access.</span>
  </div>

  <ng-container *ngIf="canView">
    <div class="mat-elevation-z8 scroll-box">
      <table mat-table [dataSource]="listDataSource" matSort matSortActive="requested_at" matSortDirection="desc" matSortDisableClear>

        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef [style.width.px]="65">
            <mat-checkbox (change)="$event ? listDataSource.selectAllToggle() : null"
              [checked]="listDataSource.selection.hasValue() && listDataSource.isAllSelected()"
              [indeterminate]="listDataSource.selection.hasValue() && !listDataSource.isAllSelected()"
              matTooltip="Toggle select all requests in this list.">
            </mat-checkbox>
          </th>
          <td mat-cell *matCellDef="let row">
            <mat-checkbox (click)="$event.stopPropagation()"
              (change)="$event ? listDataSource.selection.toggle(row.id) : null"
              [checked]="listDataSource.selection.isSelected(row.id)"
              matTooltip="Toggle select '{{ row.contact_person }}'.">
            </mat-checkbox>
          </td>
        </ng-container>

        <ng-container matColumnDef="contact_person">
          <th mat-header-cell *matHeaderCellDef mat-sort-header matTooltip="Toggle sorting by name.">
            Requested By
          </th>
          <td mat-cell *matCellDef="let request">
            <p>
              <b>{{ request.contact_person }}</b><br>
              {{ request.email }}<br>
              {{ request.mobile }}
            </p>
          </td>
        </ng-container>

        <ng-container matColumnDef="claimed_company">
          <th mat-header-cell *matHeaderCellDef matTooltip="What the person says they do. This is unverified.">
            Claimed Company
          </th>
          <td mat-cell *matCellDef="let request">
            {{ request.claimed_company || 'Not provided' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="request_reason">
          <th mat-header-cell *matHeaderCellDef
              matTooltip="What the person says about their request. This is unverified.">
            Message
          </th>
          <td mat-cell *matCellDef="let request" class="message-cell">
            <!-- Its own column rather than another line under Status, which is where the decline reason
                 lives. This travels the other way: it is the requester talking to the account, and burying
                 the one piece of new information in a re-request underneath the account's own past verdict
                 is how it gets skipped.

                 Clamped to a few lines with the full text on hover, because this is free text up to 500
                 characters and one long message would otherwise set the height of every row on the page. -->
            @if(request.request_reason) {
              <small class="d-block clamp-3-lines" [matTooltip]="request.request_reason">
                {{ request.request_reason }}
              </small>
            } @else {
              <span class="text-muted">Not provided</span>
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let request">
            <!-- Reuses the status-chip pattern from the System Banners list, which is the established
                 convention for a status column: a soft pill with a fixed min-width so the column reads as
                 a straight edge rather than ragged. Bootstrap badges were used here first and rendered as
                 harsh blocks of colour against the Material tables. -->
            <span class="status-chip" [ngClass]="'status-chip-' + (request.approval_status || 'pending')"
                  [matTooltip]="getStatusTooltip(request.approval_status)">
              {{ request.approval_status | titlecase }}
            </span>

            <!-- Who in this account already dealt with it. Several people can hold the permission, so
                 without this a reviewer cannot tell whether a colleague decided this or whether it somehow
                 decided itself, and has nobody to ask about a call they disagree with.

                 Only rendered when there is an actor to name. Memberships that predate this feature were
                 marked approved by the migration with nobody to credit, so their audit line is omitted
                 rather than filled with a guess. The Source column already reads System or Super Admin on
                 exactly those rows, which is the honest explanation. -->
            @if(request.approval_status === 'approved' && request.approved_by_name) {
              <small class="d-block text-muted mt-1">
                Approved by {{ request.approved_by_name }}
                @if(request.approved_at) {
                  on {{ (request.approved_at * 1000) | timezoneConvertMedium }}
                }
              </small>
            }

            @if(request.approval_status === 'declined' && request.declined_by_name) {
              <small class="d-block text-muted mt-1">
                Declined by {{ request.declined_by_name }}
                @if(request.declined_at) {
                  on {{ (request.declined_at * 1000) | timezoneConvertMedium }}
                }
              </small>
            }

            <!-- The reviewer needs the history to make a decision. A third decline exhausts the person's
                 ability to ask again, so the count is not incidental detail. -->
            @if(request.approval_status === 'declined') {
              @if(request.decline_count > 1) {
                <small class="d-block text-muted mt-1">Declined {{ request.decline_count }} times</small>
              }
              @if(request.decline_reason) {
                <small class="d-block text-muted mt-1">Reason: {{ request.decline_reason }}</small>
              }
            }

            <!-- A PENDING row with a decline history is a re-request, and that is the most useful single
                 fact about it: this account has already turned this person down. It only used to appear
                 once a row was declined, by which point it told the reviewer nothing they had not just
                 decided themselves. The old reason comes with it, because the new message in the Message
                 column is an answer to it and the two only make sense read together. -->
            @if(request.approval_status === 'pending' && request.decline_count > 0) {
              <small class="d-block text-muted mt-1">
                @if(request.decline_count === 1) {
                  Previously declined once
                } @else {
                  Previously declined {{ request.decline_count }} times
                }
                <!-- Named here as well as on declined rows. Asking again puts the request back in the
                     queue, quite possibly in front of somebody other than whoever refused it, and that
                     person is the one worth talking to before overturning their call. -->
                @if(request.declined_by_name) {
                  by {{ request.declined_by_name }}
                }
              </small>
              @if(request.decline_reason) {
                <small class="d-block text-muted mt-1">
                  Your reason then: {{ request.decline_reason }}
                </small>
              }
            }
          </td>
        </ng-container>

        <ng-container matColumnDef="request_origin">
          <th mat-header-cell *matHeaderCellDef matTooltip="How this person reached the account.">
            Source
          </th>
          <td mat-cell *matCellDef="let request">
            {{ getOriginLabel(request.request_origin) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="requested_at">
          <th mat-header-cell *matHeaderCellDef mat-sort-header matTooltip="Toggle sorting by request date.">
            Requested
          </th>
          <td mat-cell *matCellDef="let request">
            {{ request.requested_at ? ((request.requested_at * 1000) | timezoneConvertMedium) : 'Not recorded' }}
          </td>
        </ng-container>

        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef [style.width.px]="180"></th>
          <td mat-cell *matCellDef="let request">
            <!-- Plain text buttons, matching the Edit and Delete actions on every other list. These were
                 originally a raised primary button next to a plain one, which made the row look like it
                 carried a call to action rather than two equivalent choices.

                 Approve is deliberately available on DECLINED rows as well as pending ones. A person who
                 has hit the re-request cap cannot ask again, so approving the existing row is the only
                 route back in. -->
            <button type="button" mat-button (click)="onApprove(request)"
                    *ngIf="request.approval_status !== 'approved'"
                    matTooltip="Approve this request and give this person access to the account.">
              Approve
            </button>

            @if (request.approval_status === 'pending' && !rbac.partnerLockApplies(!!request.is_partner_managed)) {
              <button type="button" mat-button (click)="onDecline(request)"
                      matTooltip="Decline this request.">
                Decline
              </button>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      </table>

      <mat-paginator [length]="listDataSource?.total | async" [pageSize]="listDataSource.limit"
                     [pageSizeOptions]="listDataSource.pageSizeOptions" showFirstLastButtons></mat-paginator>
    </div>

    <!-- An empty table with headers reads as a loading failure. Say plainly that there is nothing to do.
         Gated on the loading flag too, otherwise "no requests" flashes up before the first load resolves
         and briefly tells the reviewer the opposite of the truth. -->
    @if((listDataSource?.total | async) === 0 && (listDataSource?.loading | async) === false) {
      <div class="alert alert-info m-2">
        <mat-icon class="align-middle mr-2">info</mat-icon>
        @switch (listDataSource.status) {
          @case ('pending') { No pending access requests. }
          @case ('declined') { No declined access requests. }
          @case ('approved') { No approved memberships to show. }
          @default { No access requests found. }
        }
      </div>
    }
  </ng-container>
</div>
